import React, { useState, useEffect } from 'react';
import { Brain, Target, TrendingUp, Clock, Star, BookOpen, Users, Zap, Heart, Globe, Award, Lightbulb } from 'lucide-react';

interface ContentItem {
  id: string;
  title: string;
  description: string;
  type: 'lesson' | 'course' | 'resource' | 'tool' | 'community' | 'event';
  category: 'financial' | 'career' | 'education' | 'health' | 'housing' | 'legal' | 'community';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedTime: number; // in minutes
  relevanceScore: number; // 0-100
  progressRelevance: number; // 0-100
  emotionalImpact: 'motivational' | 'educational' | 'supportive' | 'practical' | 'inspiring';
  tags: string[];
  prerequisites: string[];
  outcomes: string[];
  isCompleted: boolean;
  isInProgress: boolean;
  lastAccessed?: Date;
  rating?: number;
  progressContext?: string;
  languageSupport: string[];
  accessibilityFeatures: string[];
}

interface UserBehavior {
  completedItems: string[];
  timeSpent: Record<string, number>; // itemId -> minutes
  ratings: Record<string, number>; // itemId -> rating
  searchHistory: string[];
  clickHistory: string[];
  bounceRate: Record<string, number>; // itemId -> bounce rate
  completionRate: Record<string, number>; // itemId -> completion rate
  preferredCategories: string[];
  preferredDifficulty: string;
  preferredTimeSlots: string[];
  progressEngagement: Record<string, number>; // progress-based content engagement
}

interface AIContentRecommendationEngineProps {
  userProfile: {
    id: string;
    currentLocation: string;
    interests: string[];
    challenges: string[];
    goals: string[];
    learningStyle: string;
    languagePreference: string;
    currentMood: string;
    experienceLevel: string;
    studyProgress: {
      completed_lessons: number;
      total_lessons: number;
      current_streak: number;
      total_points: number;
    };
  };
  userBehavior: UserBehavior;
  onContentSelect?: (content: ContentItem) => void;
  maxRecommendations?: number;
}

const AIContentRecommendationEngine: React.FC<AIContentRecommendationEngineProps> = ({
  userProfile,
  userBehavior,
  onContentSelect,
  maxRecommendations = 8
}) => {
  const [recommendations, setRecommendations] = useState<ContentItem[]>([]);
  const [filteredRecommendations, setFilteredRecommendations] = useState<ContentItem[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [explanation, setExplanation] = useState<string>('');

  // Sample content database
  const contentDatabase: ContentItem[] = [
    {
      id: '1',
      title: 'Building Credit from Scratch',
      description: 'Complete guide to establishing and improving your credit score in America',
      type: 'course',
      category: 'financial',
      difficulty: 'beginner',
      estimatedTime: 45,
      relevanceScore: 95,
      culturalRelevance: 88,
      emotionalImpact: 'practical',
      tags: ['credit', 'banking', 'financial-literacy', 'immigration'],
      prerequisites: ['basic-english', 'bank-account'],
      outcomes: ['understand-credit-system', 'build-credit-score', 'financial-confidence'],
      isCompleted: false,
      isInProgress: false,
      culturalContext: 'Credit systems work differently in America. This course explains everything in simple terms.',
      languageSupport: ['English', 'Spanish'],
      accessibilityFeatures: ['audio', 'subtitles', 'large-text']
    },
    {
      id: '2',
      title: 'Job Search Strategies for Immigrants',
      description: 'Master the American job search process with cultural insights and practical tips',
      type: 'course',
      category: 'career',
      difficulty: 'intermediate',
      estimatedTime: 60,
      relevanceScore: 92,
      culturalRelevance: 95,
      emotionalImpact: 'motivational',
      tags: ['job-search', 'career', 'networking', 'resume'],
      prerequisites: ['work-authorization', 'basic-english'],
      outcomes: ['find-job-opportunities', 'improve-interview-skills', 'build-network'],
      isCompleted: false,
      isInProgress: false,
      culturalContext: 'American job search culture emphasizes networking and personal branding.',
      languageSupport: ['English', 'Spanish', 'Chinese'],
      accessibilityFeatures: ['audio', 'subtitles', 'interactive-exercises']
    },
    {
      id: '3',
      title: 'Understanding US Healthcare System',
      description: 'Navigate healthcare, insurance, and medical services in America',
      type: 'lesson',
      category: 'health',
      difficulty: 'beginner',
      estimatedTime: 30,
      relevanceScore: 88,
      culturalRelevance: 82,
      emotionalImpact: 'educational',
      tags: ['healthcare', 'insurance', 'medical', 'wellness'],
      prerequisites: ['basic-english'],
      outcomes: ['understand-healthcare', 'choose-insurance', 'find-doctors'],
      isCompleted: false,
      isInProgress: false,
      culturalContext: 'Healthcare in America is complex but manageable with the right knowledge.',
      languageSupport: ['English', 'Spanish'],
      accessibilityFeatures: ['audio', 'visual-guides']
    },
    {
      id: '4',
      title: 'Cultural Integration Workshop',
      description: 'Learn to balance your cultural identity with American society',
      type: 'event',
      category: 'cultural',
      difficulty: 'beginner',
      estimatedTime: 90,
      relevanceScore: 90,
      culturalRelevance: 98,
      emotionalImpact: 'supportive',
      tags: ['culture', 'identity', 'community', 'integration'],
      prerequisites: [],
      outcomes: ['cultural-confidence', 'community-connections', 'identity-balance'],
      isCompleted: false,
      isInProgress: false,
      culturalContext: 'Maintaining your cultural identity while adapting to American culture is a strength.',
      languageSupport: ['English', 'Spanish', 'Chinese', 'Arabic'],
      accessibilityFeatures: ['live-translation', 'cultural-mediators']
    },
    {
      id: '5',
      title: 'Budget Calculator Tool',
      description: 'Interactive tool to create and manage your personal budget',
      type: 'tool',
      category: 'financial',
      difficulty: 'beginner',
      estimatedTime: 20,
      relevanceScore: 85,
      culturalRelevance: 75,
      emotionalImpact: 'practical',
      tags: ['budgeting', 'calculator', 'financial-planning', 'money-management'],
      prerequisites: ['basic-math'],
      outcomes: ['create-budget', 'track-expenses', 'financial-planning'],
      isCompleted: false,
      isInProgress: false,
      culturalContext: 'Budgeting helps you achieve financial stability in America.',
      languageSupport: ['English', 'Spanish'],
      accessibilityFeatures: ['voice-input', 'large-buttons']
    },
    {
      id: '6',
      title: 'English Conversation Practice',
      description: 'Practice English with native speakers and other learners',
      type: 'community',
      category: 'education',
      difficulty: 'beginner',
      estimatedTime: 45,
      relevanceScore: 80,
      culturalRelevance: 85,
      emotionalImpact: 'supportive',
      tags: ['english', 'conversation', 'language-learning', 'practice'],
      prerequisites: ['basic-english'],
      outcomes: ['improve-english', 'build-confidence', 'make-friends'],
      isCompleted: false,
      isInProgress: false,
      culturalContext: 'Language practice with native speakers accelerates your learning.',
      languageSupport: ['English'],
      accessibilityFeatures: ['slow-speech', 'repetition']
    }
  ];

  useEffect(() => {
    generateRecommendations();
  }, [userProfile, userBehavior]);

  useEffect(() => {
    filterRecommendations();
  }, [selectedFilter, recommendations]);

  const calculateRelevanceScore = (content: ContentItem): number => {
    let score = 0;

    // Interest matching (30% weight)
    const interestMatch = content.tags.filter(tag => 
      userProfile.interests.some(interest => 
        interest.toLowerCase().includes(tag.toLowerCase()) || 
        tag.toLowerCase().includes(interest.toLowerCase())
      )
    ).length;
    score += (interestMatch / Math.max(content.tags.length, 1)) * 30;

    // Challenge matching (25% weight)
    const challengeMatch = content.tags.filter(tag => 
      userProfile.challenges.some(challenge => 
        challenge.toLowerCase().includes(tag.toLowerCase()) || 
        tag.toLowerCase().includes(challenge.toLowerCase())
      )
    ).length;
    score += (challengeMatch / Math.max(content.tags.length, 1)) * 25;

    // Goal alignment (20% weight)
    const goalMatch = content.outcomes.filter(outcome => 
      userProfile.goals.some(goal => 
        goal.toLowerCase().includes(outcome.toLowerCase()) || 
        outcome.toLowerCase().includes(goal.toLowerCase())
      )
    ).length;
    score += (goalMatch / Math.max(content.outcomes.length, 1)) * 20;

    // Cultural relevance (15% weight)
    score += content.culturalRelevance * 0.15;

    // Difficulty appropriateness (10% weight)
    const difficultyScore = userProfile.experienceLevel === 'beginner' ? 
      (content.difficulty === 'beginner' ? 100 : content.difficulty === 'intermediate' ? 50 : 0) :
      userProfile.experienceLevel === 'intermediate' ?
      (content.difficulty === 'intermediate' ? 100 : content.difficulty === 'beginner' ? 75 : 50) :
      (content.difficulty === 'advanced' ? 100 : content.difficulty === 'intermediate' ? 75 : 50);
    score += difficultyScore * 0.10;

    return Math.min(Math.round(score), 100);
  };

  const generateRecommendations = () => {
    setLoading(true);

    // Calculate relevance scores for all content
    const scoredContent = contentDatabase.map(content => ({
      ...content,
      relevanceScore: calculateRelevanceScore(content)
    }));

    // Sort by relevance score and other factors
    const sortedContent = scoredContent.sort((a, b) => {
      // Primary sort: relevance score
      if (b.relevanceScore !== a.relevanceScore) {
        return b.relevanceScore - a.relevanceScore;
      }
      
      // Secondary sort: cultural relevance
      if (b.culturalRelevance !== a.culturalRelevance) {
        return b.culturalRelevance - a.culturalRelevance;
      }
      
      // Tertiary sort: estimated time (shorter first for better engagement)
      return a.estimatedTime - b.estimatedTime;
    });

    // Filter out completed content and apply additional filters
    const filteredContent = sortedContent
      .filter(content => !content.isCompleted)
      .slice(0, maxRecommendations);

    setRecommendations(filteredContent);
    setLoading(false);

    // Generate explanation
    generateExplanation(filteredContent);
  };

  const generateExplanation = (recommendations: ContentItem[]) => {
    const topRecommendation = recommendations[0];
    if (!topRecommendation) return;

    const explanations = [
      `Based on your interest in ${userProfile.interests.join(', ')}, I recommend "${topRecommendation.title}" because it directly addresses your goals and challenges.`,
      `This content has a ${topRecommendation.relevanceScore}% relevance score, meaning it's highly aligned with your current needs and interests.`,
      `The ${topRecommendation.estimatedTime}-minute duration fits well with your learning style and available time.`,
      `It includes cultural context specific to your ${userProfile.countryOfOrigin} background, making it more relatable and effective.`
    ];

    setExplanation(explanations.join(' '));
  };

  const filterRecommendations = () => {
    if (selectedFilter === 'all') {
      setFilteredRecommendations(recommendations);
    } else {
      setFilteredRecommendations(recommendations.filter(content => content.category === selectedFilter));
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'financial':
        return <Target className="w-5 h-5" />;
      case 'career':
        return <TrendingUp className="w-5 h-5" />;
      case 'education':
        return <BookOpen className="w-5 h-5" />;
      case 'health':
        return <Heart className="w-5 h-5" />;
      case 'housing':
        return <Globe className="w-5 h-5" />;
      case 'legal':
        return <Award className="w-5 h-5" />;
      case 'cultural':
        return <Users className="w-5 h-5" />;
      case 'community':
        return <Users className="w-5 h-5" />;
      default:
        return <Lightbulb className="w-5 h-5" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'financial':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'career':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'education':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'health':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'housing':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'legal':
        return 'text-indigo-600 bg-indigo-50 border-indigo-200';
      case 'cultural':
        return 'text-pink-600 bg-pink-50 border-pink-200';
      case 'community':
        return 'text-cyan-600 bg-cyan-50 border-cyan-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getEmotionalImpactColor = (impact: string) => {
    switch (impact) {
      case 'motivational':
        return 'text-yellow-600 bg-yellow-100';
      case 'educational':
        return 'text-blue-600 bg-blue-100';
      case 'supportive':
        return 'text-green-600 bg-green-100';
      case 'practical':
        return 'text-purple-600 bg-purple-100';
      case 'inspiring':
        return 'text-pink-600 bg-pink-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'text-green-600 bg-green-100';
      case 'intermediate':
        return 'text-yellow-600 bg-yellow-100';
      case 'advanced':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">AI-Powered Content Recommendations</h2>
        <p className="text-gray-600">
          Personalized content suggestions based on your profile, behavior, and cultural background
        </p>
      </div>

      {/* AI Explanation */}
      {explanation && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-200">
          <div className="flex items-start gap-3">
            <Brain className="w-6 h-6 text-blue-600 mt-1" />
            <div>
              <h3 className="font-semibold text-blue-800 mb-2">Why These Recommendations?</h3>
              <p className="text-blue-700 text-sm leading-relaxed">{explanation}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex flex-wrap gap-2 justify-center">
        <button
          onClick={() => setSelectedFilter('all')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            selectedFilter === 'all'
              ? 'bg-primary-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All Content
        </button>
        {['financial', 'career', 'education', 'health', 'cultural', 'community'].map((category) => (
          <button
            key={category}
            onClick={() => setSelectedFilter(category)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              selectedFilter === category
                ? 'bg-primary-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>

      {/* Recommendations */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredRecommendations.map((content) => (
          <div
            key={content.id}
            onClick={() => onContentSelect?.(content)}
            className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-200 cursor-pointer group"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${getCategoryColor(content.category)}`}>
                  {getCategoryIcon(content.category)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 group-hover:text-primary-600 transition-colors">
                    {content.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(content.difficulty)}`}>
                      {content.difficulty}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEmotionalImpactColor(content.emotionalImpact)}`}>
                      {content.emotionalImpact}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="text-right">
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span>{content.relevanceScore}</span>
                </div>
                <div className="text-xs text-gray-500">relevance</div>
              </div>
            </div>

            {/* Description */}
            <p className="text-gray-600 text-sm mb-4 leading-relaxed">
              {content.description}
            </p>

            {/* Cultural Context */}
            {content.culturalContext && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-800">
                  <strong>Cultural Insight:</strong> {content.culturalContext}
                </p>
              </div>
            )}

            {/* Tags */}
            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                {content.tags.slice(0, 3).map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                  >
                    {tag}
                  </span>
                ))}
                {content.tags.length > 3 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                    +{content.tags.length - 3} more
                  </span>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{content.estimatedTime} min</span>
                </div>
                <div className="flex items-center gap-1">
                  <Globe className="w-4 h-4" />
                  <span>{content.culturalRelevance}% cultural</span>
                </div>
              </div>
              
              <div className="text-xs text-gray-500">
                {content.type.charAt(0).toUpperCase() + content.type.slice(1)}
              </div>
            </div>

            {/* Language Support */}
            {content.languageSupport.length > 1 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <span>Languages:</span>
                  <div className="flex gap-1">
                    {content.languageSupport.map((lang, index) => (
                      <span key={index} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredRecommendations.length === 0 && (
        <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
          <Brain className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">
            No recommendations found
          </h3>
          <p className="text-gray-600">
            Try adjusting your filters or complete your profile for better recommendations.
          </p>
        </div>
      )}
    </div>
  );
};

export default AIContentRecommendationEngine;
